import React, { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import api from '../services/api';
import './AdminTreasureHunt.css';

const AdminTreasureHunt = ({ standalone = true }) => {
  const { user } = useAuth();
  const [treasureHuntData, setTreasureHuntData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [allEvents, setAllEvents] = useState([]);
  const [resetting, setResetting] = useState(false);
  const [creating, setCreating] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [stopping, setStopping] = useState(false);
  const [deletingCurrent, setDeletingCurrent] = useState(false);
  const [newEventName, setNewEventName] = useState('Ongoing Treasure Hunt');
  const [newEventXPChests, setNewEventXPChests] = useState('9995');
  const [newEventUltraRareChests, setNewEventUltraRareChests] = useState('5');
  
  // Bank management states
  const [banks, setBanks] = useState([]);
  const [banksLoading, setBanksLoading] = useState(false);
  const [creatingBanks, setCreatingBanks] = useState(false);

  // Casino management states
  const [casinos, setCasinos] = useState([]);
  const [casinosLoading, setCasinosLoading] = useState(false);
  const [creatingCasinos, setCreatingCasinos] = useState(false);

  // ATM management states
  const [atms, setAtms] = useState([]);
  const [atmsLoading, setAtmsLoading] = useState(false);
  const [creatingAtms, setCreatingAtms] = useState(false);
  
  // Tab management
  const [activeTab, setActiveTab] = useState('treasureChests');
  
  // Search functionality
  const [searchCoordinates, setSearchCoordinates] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [searching, setSearching] = useState(false);

  // Fetch treasure hunt admin data
  useEffect(() => {
    const fetchAdminData = async () => {
      try {
        setLoading(true);
        setError(null);
        
        if (!user) {
          setLoading(false);
          return;
        }
        
        if (user.role !== 'admin') {
          setError('Admin authentication required');
          setLoading(false);
          return;
        }
        
        // Fetch current treasure hunt event with admin details
        const response = await api.get('/treasure-hunt/admin/current');
        const data = response.data;
        if (data.success) {
          setTreasureHuntData(data.data);
        }
        
        // Fetch all events
        const allEventsResponse = await api.get('/treasure-hunt/admin/all');
        const allEventsData = allEventsResponse.data;
        if (allEventsData.success) {
          setAllEvents(allEventsData.data);
        }
      } catch (err) {
        console.error('Error fetching admin treasure hunt data:', err);
        setError(err.response?.data?.message || err.message);
      } finally {
        setLoading(false);
      }
    };

    fetchAdminData();
  }, [user]);

  // Fetch banks, casinos, and ATMs when component mounts
  useEffect(() => {
    if (user && user.role === 'admin') {
      fetchBanks();
      fetchCasinos();
      fetchAtms();
    }
  }, [user]);

  // Get auth token helper
  const getAuthToken = () => {
    if (!user) {
      setError('Please wait, loading authentication...');
      return null;
    }
    
    if (!user.token || user.role !== 'admin') {
      setError('Admin authentication required');
      return null;
    }
    
    return user.token;
  };

  // Handle reset treasure hunt
  const handleResetTreasureHunt = async () => {
    if (!window.confirm('Are you sure you want to reset the treasure hunt?')) {
      return;
    }
    
    const token = getAuthToken();
    if (!token) return;
    
    try {
      setResetting(true);
      
      const response = await api.post('/treasure-hunt/admin/reset', {});
      const data = response.data;
      
      if (data.success) {
        setTreasureHuntData(data.data);
        setError('✅ Treasure hunt has been reset successfully!');
        
        // Refresh all events
        const allEventsResponse = await api.get('/treasure-hunt/admin/all');
        const allEventsData = allEventsResponse.data;
        if (allEventsData.success) {
          setAllEvents(allEventsData.data);
        }
      } else {
        setError(data.message || 'Failed to reset treasure hunt');
      }
    } catch (err) {
      console.error('Error resetting treasure hunt:', err);
      setError('Failed to reset treasure hunt');
    } finally {
      setResetting(false);
    }
  };

  // Handle create new event
  const handleCreateEvent = async () => {
    if (!newEventName.trim()) {
      setError('Please enter an event name');
      return;
    }
    
    const xpChests = parseInt(newEventXPChests);
    const ultraRareChests = parseInt(newEventUltraRareChests);
    
    const token = getAuthToken();
    if (!token) return;
    
    try {
      setCreating(true);
      
      const response = await api.post('/treasure-hunt/admin/create', {
        eventName: newEventName,
        xpChests,
        ultraRareChests
      });
      const data = response.data;
      
      if (data.success) {
        setError(`✅ Created treasure hunt event: ${newEventName}`);
        setNewEventName('Ongoing Treasure Hunt');
        setNewEventXPChests('9995');
        setNewEventUltraRareChests('5');
        
        // Refresh data
        const currentResponse = await api.get('/treasure-hunt/admin/current');
        const currentData = currentResponse.data;
        if (currentData.success) {
          setTreasureHuntData(currentData.data);
        }
      } else {
        setError(data.message || 'Failed to create treasure hunt event');
      }
    } catch (err) {
      console.error('Error creating treasure hunt event:', err);
      setError('Failed to create treasure hunt event');
    } finally {
      setCreating(false);
    }
  };

  // Handle stop current event
  const handleStopCurrentEvent = async () => {
    if (!window.confirm('Are you sure you want to STOP the current treasure hunt event?')) {
      return;
    }
    
    const token = getAuthToken();
    if (!token) return;
    
    try {
      setStopping(true);
      setError(null);
      
      const response = await api.post('/treasure-hunt/admin/stop', {});
      const data = response.data;
      
      if (data.success) {
        setError(`✅ ${data.message}`);
        setTreasureHuntData(null);
      } else {
        setError(data.message || 'Failed to stop treasure hunt event');
      }
    } catch (err) {
      console.error('Error stopping treasure hunt:', err);
      setError('Failed to stop treasure hunt event');
    } finally {
      setStopping(false);
    }
  };

  // Handle delete current event
  const handleDeleteCurrentEvent = async () => {
    if (!window.confirm('Are you sure you want to PERMANENTLY DELETE the current treasure hunt event?')) {
      return;
    }
    
    const token = getAuthToken();
    if (!token) return;
    
    try {
      setDeletingCurrent(true);
      setError(null);
      
      const response = await api.delete('/treasure-hunt/admin/delete');
      const data = response.data;
      
      if (data.success) {
        setError(`✅ ${data.message}`);
        setTreasureHuntData(null);
      } else {
        setError(data.message || 'Failed to delete treasure hunt event');
      }
    } catch (err) {
      console.error('Error deleting treasure hunt:', err);
      setError('Failed to delete treasure hunt event');
    } finally {
      setDeletingCurrent(false);
    }
  };

  // Handle delete current event and create new one
  const handleDeleteAndCreateNew = async () => {
    if (!window.confirm('Are you sure you want to DELETE the current treasure hunt and create a completely new one?')) {
      return;
    }
    
    const token = getAuthToken();
    if (!token) return;
    
    try {
      setDeleting(true);
      setError(null);
      
      const response = await api.delete('/treasure-hunt/admin/delete-and-create-new');
      const data = response.data;
      
      if (data.success) {
        setTreasureHuntData(data.data);
        setError(`✅ ${data.message}`);
      } else {
        setError(data.message || 'Failed to delete and create new treasure hunt');
      }
    } catch (err) {
      console.error('Error deleting and creating new treasure hunt:', err);
      setError('Failed to delete and create new treasure hunt');
    } finally {
      setDeleting(false);
    }
  };

  // Fetch banks
  const fetchBanks = async () => {
    setBanksLoading(true);
    try {
      const token = user?.token;
      if (!token) {
        setBanksLoading(false);
        return;
      }

      const response = await api.get('/treasure-hunt/banks');
      const data = response.data;
      if (data.success) {
        setBanks(data.data);
      }
    } catch (err) {
      console.error('Error fetching banks:', err);
    } finally {
      setBanksLoading(false);
    }
  };

  // Create capital city banks
  const handleCreateBanks = async () => {
    setCreatingBanks(true);
    try {
      const token = user?.token;
      if (!token) {
        setError('Authentication required');
        setCreatingBanks(false);
        return;
      }

      const response = await api.post('/treasure-hunt/admin/banks/create', {});
      const data = response.data;

      if (data.success) {
        setError(`✅ ${data.message}`);
        fetchBanks();
      } else {
        setError(data.message || 'Failed to create banks');
      }
    } catch (err) {
      console.error('Error creating banks:', err);
      setError('Failed to create banks');
    } finally {
      setCreatingBanks(false);
    }
  };

  // Fetch casinos
  const fetchCasinos = async () => {
    setCasinosLoading(true);
    try {
      const token = user?.token;
      if (!token) {
        setCasinosLoading(false);
        return;
      }

      const response = await api.get('/treasure-hunt/casinos');
      const data = response.data;
      if (data.success) {
        setCasinos(data.data);
      }
    } catch (err) {
      console.error('Error fetching casinos:', err);
    } finally {
      setCasinosLoading(false);
    }
  };

  // Create world famous casinos
  const handleCreateCasinos = async () => {
    setCreatingCasinos(true);
    try {
      const token = user?.token;
      if (!token) {
        setError('Authentication required');
        setCreatingCasinos(false);
        return;
      }

      const response = await api.post('/treasure-hunt/admin/casinos/create', {});
      const data = response.data;

      if (data.success) {
        setError(`✅ ${data.message}`);
        fetchCasinos();
      } else {
        setError(data.message || 'Failed to create casinos');
      }
    } catch (err) {
      console.error('Error creating casinos:', err);
      setError('Failed to create casinos');
    } finally {
      setCreatingCasinos(false);
    }
  };

  // Fetch ATMs
  const fetchAtms = async () => {
    setAtmsLoading(true);
    try {
      const token = user?.token;
      if (!token) {
        setAtmsLoading(false);
        return;
      }

      const response = await api.get('/treasure-hunt/atms');
      const data = response.data;
      if (data.success) {
        setAtms(data.data);
      }
    } catch (err) {
      console.error('Error fetching ATMs:', err);
    } finally {
      setAtmsLoading(false);
    }
  };

  // Create global ATMs
  const handleCreateAtms = async () => {
    setCreatingAtms(true);
    try {
      const token = user?.token;
      if (!token) {
        setError('Authentication required');
        setCreatingAtms(false);
        return;
      }

      const response = await api.post('/treasure-hunt/admin/atms/create', {});
      const data = response.data;

      if (data.success) {
        setError(`✅ ${data.message}`);
        fetchAtms();
      } else {
        setError(data.message || 'Failed to create ATMs');
      }
    } catch (err) {
      console.error('Error creating ATMs:', err);
      setError('Failed to create ATMs');
    } finally {
      setCreatingAtms(false);
    }
  };

  // Search by coordinates
  const handleCoordinateSearch = async () => {
    if (!searchCoordinates.trim()) {
      setError('Please enter coordinates to search');
      return;
    }

    const coordMatch = searchCoordinates.match(/(-?\d+\.?\d*),\s*(-?\d+\.?\d*)/);
    if (!coordMatch) {
      setError('Please enter coordinates in format: lat, lng (e.g., 40.7128, -74.0060)');
      return;
    }

    const lat = parseFloat(coordMatch[1]);
    const lng = parseFloat(coordMatch[2]);

    if (lat < -90 || lat > 90 || lng < -180 || lng > 180) {
      setError('Invalid coordinates. Latitude must be between -90 and 90, longitude between -180 and 180');
      return;
    }

    setSearching(true);
    setSearchResults([]);

    try {
      const results = [];
      
      // Search in ATMs
      const atmMatches = atms.filter(atm => 
        Math.abs(atm.coordinates.lat - lat) < 0.1 && 
        Math.abs(atm.coordinates.lng - lng) < 0.1
      );
      results.push(...atmMatches.map(item => ({ ...item, type: 'ATM' })));

      // Search in banks
      const bankMatches = banks.filter(bank => 
        Math.abs(bank.coordinates.lat - lat) < 0.1 && 
        Math.abs(bank.coordinates.lng - lng) < 0.1
      );
      results.push(...bankMatches.map(item => ({ ...item, type: 'Bank' })));

      // Search in casinos
      const casinoMatches = casinos.filter(casino => 
        Math.abs(casino.coordinates.lat - lat) < 0.1 && 
        Math.abs(casino.coordinates.lng - lng) < 0.1
      );
      results.push(...casinoMatches.map(item => ({ ...item, type: 'Casino' })));

      // Search in treasure chests
      if (treasureHuntData?.treasureChests) {
        const chestMatches = treasureHuntData.treasureChests.filter(chest => 
          chest.coordinates.lat && chest.coordinates.lng &&
          Math.abs(chest.coordinates.lat - lat) < 0.1 && 
          Math.abs(chest.coordinates.lng - lng) < 0.1
        );
        results.push(...chestMatches.map(item => ({ ...item, type: 'Treasure Chest' })));
      }

      setSearchResults(results);
      
      if (results.length === 0) {
        setError('No items found within 0.1 degrees of the specified coordinates');
      } else {
        setError(`✅ Found ${results.length} items near ${lat}, ${lng}`);
      }

    } catch (err) {
      console.error('Error searching coordinates:', err);
      setError('Failed to search coordinates');
    } finally {
      setSearching(false);
    }
  };

  // Tab content components
  const renderTreasureChestsTab = () => (
    <>
      {/* Current Event Overview */}
      <div className="admin-section">
        <h3>📍 Current Event Status</h3>
        {treasureHuntData ? (
          <div className="current-event-overview">
            <div className="event-header">
              <h4>Event: {treasureHuntData.eventId}</h4>
              <span className={`event-status ${treasureHuntData.isActive ? 'active' : 'inactive'}`}>
                {treasureHuntData.isActive ? '🟢 Active' : '🔴 Inactive'}
              </span>
            </div>
            
            <div className="event-stats">
              <div className="stat-item">
                <span className="stat-label">Event Name:</span>
                <span className="stat-value">{treasureHuntData.eventName}</span>
              </div>
              <div className="stat-item">
                <span className="stat-label">Total Chests:</span>
                <span className="stat-value">{treasureHuntData.totalChests}</span>
              </div>
              <div className="stat-item">
                <span className="stat-label">💰 Treasure Chests:</span>
                <span className="stat-value">{treasureHuntData.treasureChestCount || 0}</span>
              </div>
              <div className="stat-item">
                <span className="stat-label">⭐ Regular XP Chests (1 XP):</span>
                <span className="stat-value">{treasureHuntData.regularXPChestCount || 0}</span>
              </div>
              <div className="stat-item">
                <span className="stat-label">💎 Ultra-Rare XP Chests (50k XP):</span>
                <span className="stat-value">{treasureHuntData.ultraRareXPChestCount || 0}</span>
              </div>
              <div className="stat-item">
                <span className="stat-label">Chests Opened:</span>
                <span className="stat-value">{treasureHuntData.chestsOpened}/{treasureHuntData.totalChests}</span>
              </div>
              <div className="stat-item">
                <span className="stat-label">Active Chests:</span>
                <span className="stat-value">{treasureHuntData.activeChests || (treasureHuntData.totalChests - treasureHuntData.chestsOpened)}</span>
              </div>
              <div className="stat-item">
                <span className="stat-label">Total Prize Pool:</span>
                <span className="stat-value">{treasureHuntData.totalPrizePool} WR Coins</span>
              </div>
              <div className="stat-item">
                <span className="stat-label">Start Date:</span>
                <span className="stat-value">{new Date(treasureHuntData.startDate).toLocaleDateString()}</span>
              </div>
              <div className="stat-item">
                <span className="stat-label">Event Type:</span>
                <span className="stat-value">Ongoing Event</span>
              </div>
            </div>
          </div>
        ) : (
          <div className="no-active-event">
            <p>🚫 No active treasure hunt event found.</p>
            <p className="no-event-hint">Use the "Create New Event" form below to start an ongoing treasure hunt!</p>
          </div>
        )}
      </div>

      {/* Admin Actions */}
      <div className="admin-section">
        <h3>⚙️ Admin Actions</h3>
        <div className="admin-actions">
          {treasureHuntData && treasureHuntData.isActive && (
            <>
              <button 
                onClick={handleStopCurrentEvent}
                disabled={stopping}
                className="admin-button stop-button"
              >
                {stopping ? '⏹️ Stopping...' : '⏹️ Stop Current Event'}
              </button>
              
              <button 
                onClick={handleDeleteCurrentEvent}
                disabled={deletingCurrent}
                className="admin-button delete-current-button"
              >
                {deletingCurrent ? '🗑️ Deleting...' : '🗑️ Delete Current Event'}
              </button>
            </>
          )}
          
          <button 
            onClick={handleResetTreasureHunt}
            disabled={resetting}
            className="admin-button reset-button"
          >
            {resetting ? '🔄 Resetting...' : '🔄 Reset Current Event'}
          </button>
          
          <button 
            onClick={handleDeleteAndCreateNew}
            disabled={deleting}
            className="admin-button delete-button"
          >
            {deleting ? '🗑️ Deleting & Creating...' : '🗑️ Delete & Create New (Random Locations)'}
          </button>
          
          <div className="create-event-form">
            <h4>Create New Event</h4>
            <div className="form-group">
              <input
                type="text"
                placeholder="Event Name"
                value={newEventName}
                onChange={(e) => setNewEventName(e.target.value)}
                title="Name for the new treasure hunt event"
              />
              <input
                type="number"
                placeholder="Regular XP Chests (1 XP each)"
                value={newEventXPChests}
                onChange={(e) => setNewEventXPChests(e.target.value)}
                min="0"
                title="Number of regular XP chests to distribute globally"
              />
              <input
                type="number"
                placeholder="Ultra-Rare XP Chests (50k XP each)"
                value={newEventUltraRareChests}
                onChange={(e) => setNewEventUltraRareChests(e.target.value)}
                min="0"
                title="Number of ultra-rare XP chests with 50,000 XP each"
              />
              <button 
                onClick={handleCreateEvent}
                disabled={creating}
                className="admin-button create-button"
              >
                {creating ? '🔄 Creating...' : '➕ Create New Event'}
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Treasure Chest Details */}
      {treasureHuntData && treasureHuntData.treasureChests && (
        <div className="admin-section">
          <h3>💰 Active Treasure Chests (Unopened Only)</h3>
          <p className="chest-info">Showing {treasureHuntData.treasureChests.length} active treasure chests. XP chests are not displayed here.</p>
          <div className="treasure-chest-grid">
            {treasureHuntData.treasureChests.map((chest) => (
              <div 
                key={chest.chestId}
                className={`admin-chest-card ${chest.isOpened ? 'opened' : 'closed'}`}
              >
                <div className="chest-header">
                  <span className="chest-id">Chest #{chest.chestId}</span>
                  <span className="chest-status closed">
                    💰 Active
                  </span>
                </div>
                
                <div className="chest-details">
                  <div className="detail-row">
                    <span>📍 Coordinates:</span>
                    <span>
                      Pixel: ({chest.coordinates.x}, {chest.coordinates.y})
                      {chest.coordinates.lat && chest.coordinates.lng && (
                        <br />
                      )}
                      {chest.coordinates.lat && chest.coordinates.lng && (
                        <>GPS: ({chest.coordinates.lat.toFixed(6)}, {chest.coordinates.lng.toFixed(6)})</>
                      )}
                    </span>
                  </div>
                  <div className="detail-row">
                    <span>🔢 Combination:</span>
                    <span className="combination">{chest.combination}</span>
                  </div>
                  <div className="detail-row">
                    <span>💎 Prize:</span>
                    <span>{chest.prizeAmount} WR Coins</span>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Event History */}
      <div className="admin-section">
        <h3>📚 Event History</h3>
        {allEvents.length > 0 ? (
          <div className="events-history">
            {allEvents.map((event) => (
              <div key={event.eventId} className="event-history-item">
                <div className="event-info">
                  <span className="event-name">{event.eventName || event.eventId}</span>
                  <span className={`event-status ${event.isActive ? 'active' : 'inactive'}`}>
                    {event.isActive ? '🟢 Active' : '🔴 Inactive'}
                  </span>
                </div>
                <div className="event-summary">
                  <span>📅 {new Date(event.startDate).toLocaleDateString()}</span>
                  <span>📦 {event.chestsOpened}/{event.totalChests} opened</span>
                  <span>💰 {event.totalPrizePool} WR Coins</span>
                </div>
              </div>
            ))}
          </div>
        ) : (
          <p>No events found.</p>
        )}
      </div>
    </>
  );

  const renderBanksTab = () => (
    <div className="admin-section">
      <h3>🏦 Capital City Banks</h3>
      <p className="section-description">
        Manage banks in capital cities worldwide. Each bank has a unique 4-digit access code.
      </p>
      
      <div className="banks-controls">
        <button 
          onClick={handleCreateBanks}
          disabled={creatingBanks}
          className="admin-button create-button"
        >
          {creatingBanks ? '🔄 Creating Banks...' : '🏗️ Create All Capital City Banks'}
        </button>
        <button 
          onClick={fetchBanks}
          disabled={banksLoading}
          className="admin-button"
        >
          {banksLoading ? '🔄 Loading...' : '🔄 Refresh Banks'}
        </button>
      </div>

      {banksLoading ? (
        <div className="loading-message">Loading banks...</div>
      ) : banks.length > 0 ? (
        <div className="banks-grid">
          {banks.map((bank) => (
            <div key={bank.bankId} className="bank-card">
              <div className="bank-header">
                <span className="bank-city">🏦 {bank.cityName}</span>
                <span className="bank-country">{bank.countryName}</span>
              </div>
              <div className="bank-details">
                <div className="detail-row">
                  <span>📍 Coordinates:</span>
                  <span>({bank.coordinates.lat.toFixed(4)}, {bank.coordinates.lng.toFixed(4)})</span>
                </div>
                <div className="detail-row">
                  <span>🔢 Access Code:</span>
                  <span className="access-code">{bank.accessCode}</span>
                </div>
                <div className="detail-row">
                  <span>👥 Accessed By:</span>
                  <span>{bank.accessedBy ? bank.accessedBy.length : 0} users</span>
                </div>
                <div className="detail-row">
                  <span>📅 Status:</span>
                  <span className={`status ${bank.isActive ? 'active' : 'inactive'}`}>
                    {bank.isActive ? '🟢 Active' : '🔴 Inactive'}
                  </span>
                </div>
              </div>
            </div>
          ))}
        </div>
      ) : (
        <div className="no-banks-message">
          <p>No banks found. Create capital city banks to get started.</p>
        </div>
      )}
    </div>
  );

  const renderCasinosTab = () => (
    <div className="admin-section">
      <h3>🎰 World Famous Casinos</h3>
      <p className="section-description">
        Manage casinos worldwide including Las Vegas Strip. Each casino has slot machines and vaults to hack.
      </p>
      
      <div className="casinos-controls">
        <button 
          onClick={handleCreateCasinos}
          disabled={creatingCasinos}
          className="admin-button create-button"
        >
          {creatingCasinos ? '🔄 Creating Casinos...' : '🏗️ Create All World Famous Casinos (20 Las Vegas + International)'}
        </button>
        <button 
          onClick={fetchCasinos}
          disabled={casinosLoading}
          className="admin-button"
        >
          {casinosLoading ? '🔄 Loading...' : '🔄 Refresh Casinos'}
        </button>
      </div>

      {casinosLoading ? (
        <div className="loading-message">Loading casinos...</div>
      ) : casinos.length > 0 ? (
        <div className="casinos-grid">
          {casinos.map((casino) => (
            <div key={casino.casinoId} className="casino-card">
              <div className="casino-header">
                <span className="casino-name">🎰 {casino.name}</span>
                <span className="casino-location">{casino.cityName}, {casino.countryName}</span>
              </div>
              <div className="casino-details">
                <div className="detail-row">
                  <span>📍 Coordinates:</span>
                  <span>({casino.coordinates.lat.toFixed(4)}, {casino.coordinates.lng.toFixed(4)})</span>
                </div>
                <div className="detail-row">
                  <span>🎰 Slot Machines:</span>
                  <span>{casino.slotMachines?.count || 'N/A'} machines (${casino.slotMachines?.hackCost || 'N/A'} WR cost)</span>
                </div>
                <div className="detail-row">
                  <span>🔐 Vault:</span>
                  <span>{casino.vault?.totalMoney?.toLocaleString() || 'N/A'} WR Coins (Security Level {casino.vault?.securityLevel || 'N/A'})</span>
                </div>
                <div className="detail-row">
                  <span>📅 Status:</span>
                  <span className={`status ${casino.isActive ? 'active' : 'inactive'}`}>
                    {casino.isActive ? '🟢 Active' : '🔴 Inactive'}
                  </span>
                </div>
                <div className="detail-row">
                  <span>🎯 Hack History:</span>
                  <span>{casino.hackHistory ? casino.hackHistory.length : 0} total hacks</span>
                </div>
              </div>
            </div>
          ))}
        </div>
      ) : (
        <div className="no-casinos-message">
          <p>No casinos found. Create world famous casinos to get started.</p>
        </div>
      )}
    </div>
  );

  const renderATMsTab = () => (
    <div className="admin-section">
      <h3>🏧 Global ATM Network</h3>
      <p className="section-description">
        Manage ATMs worldwide. Each ATM has a 4-digit code found randomly in XP chests (1 in 100 chance).
      </p>
      
      <div className="atms-controls">
        <button 
          onClick={handleCreateAtms}
          disabled={creatingAtms}
          className="admin-button create-button"
        >
          {creatingAtms ? '🔄 Creating ATMs...' : '🏗️ Create 100 Global ATMs (~500 WR each)'}
        </button>
        <button 
          onClick={fetchAtms}
          disabled={atmsLoading}
          className="admin-button"
        >
          {atmsLoading ? '🔄 Loading...' : '🔄 Refresh ATMs'}
        </button>
      </div>

      {atmsLoading ? (
        <div className="loading-message">Loading ATMs...</div>
      ) : atms.length > 0 ? (
        <div className="atms-grid">
          {atms.map((atm) => (
            <div key={atm.atmId} className="atm-card">
              <div className="atm-header">
                <span className="atm-id">🏧 {atm.atmId}</span>
                <span className="atm-cash">{atm.cashAmount} WR Coins</span>
              </div>
              <div className="atm-details">
                <div className="detail-row">
                  <span>📍 Coordinates:</span>
                  <span>({atm.coordinates.lat.toFixed(4)}, {atm.coordinates.lng.toFixed(4)})</span>
                </div>
                <div className="detail-row">
                  <span>🔢 Access Code:</span>
                  <span className="access-code">{atm.accessCode}</span>
                </div>
                <div className="detail-row">
                  <span>👥 Accessed By:</span>
                  <span>{atm.accessedBy ? atm.accessedBy.length : 0} users</span>
                </div>
                <div className="detail-row">
                  <span>📅 Status:</span>
                  <span className={`status ${atm.isActive ? 'active' : 'inactive'}`}>
                    {atm.isActive ? '🟢 Active' : '🔴 Inactive'}
                  </span>
                </div>
                <div className="detail-row">
                  <span>📅 Created:</span>
                  <span>{new Date(atm.createdAt).toLocaleDateString()}</span>
                </div>
              </div>
            </div>
          ))}
          {atms.length > 20 && (
            <div className="atms-summary">
              <p>Showing all {atms.length} ATMs. All ATMs are distributed globally with random coordinates.</p>
            </div>
          )}
        </div>
      ) : (
        <div className="no-atms-message">
          <p>No ATMs found. Create global ATMs to get started.</p>
        </div>
      )}
    </div>
  );

  const renderSearchResults = () => {
    if (searchResults.length === 0) return null;

    return (
      <div className="admin-section">
        <h3>🔍 Search Results</h3>
        <div className="search-results-grid">
          {searchResults.map((item, index) => (
            <div key={index} className={`search-result-card ${item.type.toLowerCase()}-card`}>
              <div className="result-header">
                <span className="result-type">{item.type}</span>
                <span className="result-name">
                  {item.type === 'ATM' && item.atmId}
                  {item.type === 'Bank' && item.cityName}
                  {item.type === 'Casino' && item.name}
                  {item.type === 'Treasure Chest' && `Chest #${item.chestId}`}
                </span>
              </div>
              <div className="result-details">
                <div className="detail-row">
                  <span>📍 Coordinates:</span>
                  <span>({item.coordinates.lat.toFixed(4)}, {item.coordinates.lng.toFixed(4)})</span>
                </div>
                {(item.accessCode || item.combination) && (
                  <div className="detail-row">
                    <span>🔢 {item.accessCode ? 'Access Code' : 'Combination'}:</span>
                    <span className="access-code">{item.accessCode || item.combination}</span>
                  </div>
                )}
                {item.prizeAmount && (
                  <div className="detail-row">
                    <span>💰 Prize:</span>
                    <span>{item.prizeAmount} WR Coins</span>
                  </div>
                )}
                {item.cashAmount && (
                  <div className="detail-row">
                    <span>💰 Cash:</span>
                    <span>{item.cashAmount} WR Coins</span>
                  </div>
                )}
              </div>
            </div>
          ))}
        </div>
      </div>
    );
  };

  if (loading) {
    return (
      <div className="admin-treasure-hunt-loading">
        <div className="loading-spinner"></div>
        <p>Loading treasure hunt admin data...</p>
      </div>
    );
  }

  return (
    <div className={standalone ? "admin-treasure-hunt-standalone" : "admin-treasure-hunt-container"}>
      {standalone && <h2>🏴‍☠️ Treasure Hunt Administration</h2>}
      
      {error && (
        <div className={`admin-message ${error.includes('✅') ? 'success' : 'error'}`}>
          {error}
        </div>
      )}

      {/* Coordinate Search */}
      <div className="admin-section">
        <h3>🔍 Coordinate Search</h3>
        <p className="section-description">
          Search for items near specific coordinates. Search within 0.1 degrees (~11km) radius.
        </p>
        <div className="search-controls">
          <input
            type="text"
            placeholder="Enter coordinates (lat, lng) e.g. 40.7128, -74.0060"
            value={searchCoordinates}
            onChange={(e) => setSearchCoordinates(e.target.value)}
            className="coordinate-search-input"
          />
          <button
            onClick={handleCoordinateSearch}
            disabled={searching}
            className="admin-button search-button"
          >
            {searching ? '🔍 Searching...' : '🔍 Search'}
          </button>
        </div>
        
        {renderSearchResults()}
      </div>

      {/* Tab Navigation */}
      <div className="admin-tabs">
        <button
          className={`tab-button ${activeTab === 'treasureChests' ? 'active' : ''}`}
          onClick={() => setActiveTab('treasureChests')}
        >
          💰 Active Treasure Chests
        </button>
        <button
          className={`tab-button ${activeTab === 'banks' ? 'active' : ''}`}
          onClick={() => setActiveTab('banks')}
        >
          🏦 Capital City Banks
        </button>
        <button
          className={`tab-button ${activeTab === 'casinos' ? 'active' : ''}`}
          onClick={() => setActiveTab('casinos')}
        >
          🎰 World Famous Casinos
        </button>
        <button
          className={`tab-button ${activeTab === 'atms' ? 'active' : ''}`}
          onClick={() => setActiveTab('atms')}
        >
          🏧 Global ATM Network
        </button>
      </div>

      {/* Tab Content */}
      <div className="tab-content">
        {activeTab === 'treasureChests' && renderTreasureChestsTab()}
        {activeTab === 'banks' && renderBanksTab()}
        {activeTab === 'casinos' && renderCasinosTab()}
        {activeTab === 'atms' && renderATMsTab()}
      </div>
    </div>
  );
};

export default AdminTreasureHunt;